import React from 'react';
import { GraduationCap, Users, Calendar, ArrowRight, FileText } from 'lucide-react';

export const CampusConnect = () => {
  const handleInviteClick = () => {
    window.location.href = '/joinThinkTech';
  };

  const handleJoinThinkTechClick = () => {
    const registerSection = document.getElementById('register-section');
    if (registerSection) {
      registerSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden" style={{backgroundColor: '#FAF7F2'}}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 border border-gray-300 rounded-full"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 border border-gray-300 rounded-full"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 border border-gray-400 rounded-full"></div>
      </div>

      {/* Main Hero Content */}
      <div className="container mx-auto px-3 sm:px-4 md:px-6 relative z-10 pt-3 sm:pt-4 md:pt-6 lg:pt-8 pb-4 sm:pb-6 md:pb-8 lg:pb-10">
        <div className="max-w-6xl mx-auto">
          {/* Mobile ThinkTech Badge with Join Button */}
          <div className="lg:hidden flex flex-col items-center mb-2 sm:mb-3 md:mb-4 gap-2">
            <div className="flex items-center bg-white border-2 rounded-2xl px-3 py-2 sm:px-4 sm:py-2 md:px-5 md:py-2 shadow-lg" style={{borderColor: '#C4A052'}}>
              <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 mr-2 md:mr-3" style={{color: '#C4A052'}} />
              <div className="flex flex-col items-center">
                <span className="font-bold text-xs sm:text-sm md:text-base text-center" style={{color: '#6B4423'}}>India's First ThinkTech™ Platform</span>
              </div>
            </div>
            <button
              onClick={handleJoinThinkTechClick}
              className="px-4 py-2 sm:px-5 sm:py-2 text-white font-bold text-xs sm:text-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              style={{backgroundColor: '#C4A052'}}
              aria-label="Join ThinkTech community"
            >
              Join ThinkTech
            </button>
          </div>

          {/* Main Heading */}
          <div className="text-center mb-4 sm:mb-6 md:mb-8 lg:mb-10">
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 md:mb-4 leading-tight px-2" style={{color: '#2C2420'}}>
              Clarity-First Learning for AI,
              <br />
              <span style={{color: '#C4A052'}}>Startups</span> & the <span style={{color: '#C4A052'}}>Real World</span>
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl max-w-4xl mx-auto leading-relaxed mb-2 sm:mb-3 md:mb-3 font-medium px-3" style={{color: '#6B4423'}}>
              <span className="font-bold" style={{color: '#2C2420'}}>A2ZCampus™</span> equips students with the clarity, skills, and real-world mindset needed to thrive in the age of AI and innovation.
            </p>
            
            <p className="text-xs sm:text-sm md:text-base lg:text-lg max-w-4xl mx-auto leading-relaxed mb-3 sm:mb-4 md:mb-6 px-3" style={{color: '#2C2420'}}>
              Through our proprietary tools — <span style={{color: '#6B4423'}} className="font-bold">ClarityKit™</span>, <span style={{color: '#6B4423'}} className="font-bold">Startup Simulator™</span>, and <span style={{color: '#6B4423'}} className="font-bold">Real World Readiness™</span> — we help learners move beyond confusion and resume fluff into purpose-driven careers.
            </p>

            {/* CTA Buttons */}
            <div className="flex justify-center items-center mb-4 sm:mb-6 md:mb-8 lg:mb-10">
              <button
                onClick={handleInviteClick}
                className="group px-4 sm:px-5 md:px-6 lg:px-8 py-2 sm:py-3 md:py-3 text-white font-bold text-sm sm:text-base md:text-base rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 flex items-center"
                style={{backgroundColor: '#C4A052'}}
                aria-label="Get involved with A2ZCampus workshops and training programs"
              >
                Get Involved
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 ml-2 md:ml-3 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-5 mb-4 sm:mb-6 md:mb-8 lg:mb-10">
            <div className="bg-white rounded-3xl p-3 sm:p-4 md:p-5 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-200 group hover:-translate-y-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-3xl flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4 group-hover:scale-110 transform duration-300" style={{backgroundColor: '#FFFFFF', border: '2px solid #C4A052'}}>
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" style={{color: '#C4A052'}} aria-label="Partner Institutions Icon" />
              </div>
              <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-2 md:mb-3 text-center" style={{color: '#2C2420'}}>Partner Institutions</h2>
              <p className="leading-relaxed text-xs sm:text-sm md:text-base text-center" style={{color: '#2C2420'}}>
                Collaborating with top universities to identify and nurture talent.
              </p>
            </div>
            
            <div className="bg-white rounded-3xl p-3 sm:p-4 md:p-5 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-200 group hover:-translate-y-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-3xl flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4 group-hover:scale-110 transform duration-300" style={{backgroundColor: '#FFFFFF', border: '2px solid #C4A052'}}>
                <Users className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" style={{color: '#C4A052'}} aria-label="Industry Exposure Icon" />
              </div>
              <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-2 md:mb-3 text-center" style={{color: '#2C2420'}}>Industry Exposure</h2>
              <p className="leading-relaxed text-xs sm:text-sm md:text-base text-center" style={{color: '#2C2420'}}>
                Direct interaction with industry experts and real-world projects.
              </p>
            </div>
            
            <div className="bg-white rounded-3xl p-3 sm:p-4 md:p-5 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-200 group hover:-translate-y-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-3xl flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4 group-hover:scale-110 transform duration-300" style={{backgroundColor: '#FFFFFF', border: '2px solid #C4A052'}}>
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" style={{color: '#C4A052'}} aria-label="Regular Events Icon" />
              </div>
              <h2 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-2 md:mb-3 text-center" style={{color: '#2C2420'}}>Regular Events</h2>
              <p className="leading-relaxed text-xs sm:text-sm md:text-base text-center" style={{color: '#2C2420'}}>
                Ongoing workshops, training sessions, and placement drives.
              </p>
            </div>
          </div>

          {/* Partner Universities Section */}
          <div className="bg-white rounded-3xl p-3 sm:p-4 md:p-6 lg:p-8 shadow-2xl border border-gray-200">
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 md:mb-8 text-center" style={{color: '#6B4423'}}>Partner Universities</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              <div className="text-center group">
                <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-3xl flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4 shadow-xl group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: '#6B4423'}}>
                  <span className="text-white font-bold text-lg sm:text-xl md:text-2xl">R</span>
                </div>
                <h3 className="text-sm sm:text-base md:text-lg font-bold group-hover:opacity-80 transition-colors" style={{color: '#000000'}}>
                  REVA University
                </h3>
              </div>
              
              <div className="text-center group">
                <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-3xl flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4 shadow-xl group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: '#6B4423'}}>
                  <span className="text-white font-bold text-lg sm:text-xl md:text-2xl">M</span>
                </div>
                <h3 className="text-sm sm:text-base md:text-lg font-bold group-hover:opacity-80 transition-colors" style={{color: '#000000'}}>
                  M.S. Engineering College
                </h3>
              </div>
              
              <div className="text-center group sm:col-span-2 md:col-span-1">
                <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-3xl flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4 shadow-xl group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: '#6B4423'}}>
                  <span className="text-white font-bold text-lg sm:text-xl md:text-2xl">V</span>
                </div>
                <h3 className="text-sm sm:text-base md:text-lg font-bold group-hover:opacity-80 transition-colors" style={{color: '#000000'}}>
                  Vaagdevi Institute
                </h3>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-6 sm:mt-8 md:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2 md:mb-2" style={{color: '#C4A052'}}>500+</div>
              <div className="text-sm sm:text-base md:text-lg font-medium" style={{color: '#2C2420'}}>Students Trained</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2 md:mb-2" style={{color: '#C4A052'}}>15+</div>
              <div className="text-sm sm:text-base md:text-lg font-medium" style={{color: '#2C2420'}}>Workshops Conducted</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2 md:mb-2" style={{color: '#C4A052'}}>10+</div>
              <div className="text-sm sm:text-base md:text-lg font-medium" style={{color: '#2C2420'}}>Partner Colleges</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};